import React, { useState, useEffect, useRef } from 'react'
import { motion, AnimatePresence, useScroll, useTransform, useInView } from 'framer-motion'
import { useNavigate } from 'react-router-dom'
import { 
  Zap, 
  Brain, 
  Target, 
  Eye, 
  Globe, 
  Shield, 
  ArrowRight, 
  Play, 
  CheckCircle, 
  Star,
  Users,
  Clock,
  BarChart3,
  Code,
  Terminal,
  Layers,
  Image,
  RefreshCw,
  Sparkles,
  Bot,
  Monitor,
  Settings,
  Download,
  TrendingUp,
  Award,
  Rocket,
  ChevronRight,
  LogIn,
  UserPlus,
  MousePointer,
  Cpu,
  Database,
  Workflow,
  TestTube,
  Activity,
  Calendar
} from 'lucide-react'

const LogoIcon = ({ className = "h-6 w-6", stroke = "currentColor" }) => (
  <svg 
    xmlns="http://www.w3.org/2000/svg" 
    viewBox="0 0 24 24" 
    fill="none" 
    stroke={stroke} 
    strokeWidth="2" 
    strokeLinecap="round" 
    strokeLinejoin="round" 
    className={className}
  >
    <path d="M3 7V5c0-1.1.9-2 2-2h2"></path>
    <path d="M17 3h2c1.1 0 2 .9 2 2v2"></path>
    <path d="M21 17v2c0 1.1-.9 2-2 2h-2"></path>
    <path d="M7 21H5c-1.1 0-2-.9-2-2v-2"></path>
    <rect width="7" height="7" x="7" y="7" rx="1"></rect>
    <rect width="7" height="7" x="10" y="10" rx="1"></rect>
  </svg>
)

const TestCrewLanding = () => {
  const navigate = useNavigate()
  const [currentFeature, setCurrentFeature] = useState(0)
  const [isVisible, setIsVisible] = useState(false)
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 })
  const heroRef = useRef(null)
  const { scrollYProgress } = useScroll()
  const y = useTransform(scrollYProgress, [0, 1], ['0%', '50%'])
  const opacity = useTransform(scrollYProgress, [0, 0.5], [1, 0])
  const [showDemoModal, setShowDemoModal] = useState(false)
  const [showVideoModal, setShowVideoModal] = useState(false)
  const [formStatus, setFormStatus] = useState('')
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    company: '',
    message: ''
  })
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false)

  useEffect(() => {
    setIsVisible(true)
    const interval = setInterval(() => {
      setCurrentFeature((prev) => (prev + 1) % 4)
    }, 3500)
    
    const handleMouseMove = (e) => {
      setMousePosition({ x: e.clientX, y: e.clientY })
    }
    
    window.addEventListener('mousemove', handleMouseMove)
    
    return () => {
      clearInterval(interval)
      window.removeEventListener('mousemove', handleMouseMove)
    }
  }, [])

  useEffect(() => {
    // Update page title
    document.title = "TestCrew - AI Testing Platform"
  }, [])

  const features = [
    {
      icon: Brain,
      title: "AI-Powered Test Generation",
      description: "Let our advanced AI analyze your website and automatically generate comprehensive test suites",
      gradient: "from-purple-500 via-pink-500 to-blue-600",
      color: "purple"
    },
    {
      icon: Terminal,
      title: "Natural Language Testing",
      description: "Simply describe what you want to test in plain English and watch TestCrew execute it",
      gradient: "from-orange-500 via-red-500 to-pink-600",
      color: "orange"
    },
    {
      icon: Eye,
      title: "Real-Time Visual Monitoring",
      description: "Watch live screenshots and execution logs as AI navigates and tests your website",
      gradient: "from-green-500 via-teal-500 to-blue-600",
      color: "green"
    },
    {
      icon: Workflow,
      title: "Smart Test Orchestration",
      description: "Intelligently coordinate complex test workflows with advanced automation patterns",
      gradient: "from-blue-500 via-indigo-500 to-purple-600",
      color: "blue"
    }
  ]

  const stats = [
    { icon: Target, number: "99.9%", label: "Test Accuracy", delay: 0 },
    { icon: Clock, number: "15x", label: "Faster Testing", delay: 0.1 },
    { icon: Users, number: "2,500+", label: "Happy Teams", delay: 0.2 },
    { icon: TestTube, number: "5M+", label: "Tests Executed", delay: 0.3 }
  ]

  const testimonials = [
    {
      name: "Sarah Chen",
      role: "Senior QA Engineer at TechFlow",
      content: "TestCrew revolutionized our testing process. Complex scenarios that took days now complete in hours with better coverage.",
      avatar: "👩‍💻",
      rating: 5,
      company: "TechFlow"
    },
    {
      name: "Marcus Rodriguez",
      role: "Lead Developer at CloudVault",
      content: "The AI understands our app better than some of our junior developers. It catches edge cases we never thought of.",
      avatar: "👨‍💼",
      rating: 5,
      company: "CloudVault"
    },
    {
      name: "Emily Watson",
      role: "Product Manager at StartupX",
      content: "Finally, our entire team can create and run sophisticated tests without writing a single line of code.",
      avatar: "👩‍🚀",
      rating: 5,
      company: "StartupX"
    }
  ]

  const pricingPlans = [
    {
      name: "Basic",
      price: "$99",
      period: "/month",
      description: "Perfect for small teams getting started with AI testing",
      features: [
        "500 test executions/month",
        "AI-powered test generation",
        "Email support",
        "Basic analytics dashboard",
        "Up to 5 team members",
        "30-day test history"
      ],
      gradient: "from-blue-400 to-blue-600",
      popular: false,
      badge: null
    },
    {
      name: "Pro",
      price: "$499",
      period: "/month",
      description: "Unlimited testing power for growing enterprises",
      features: [
        "Unlimited test executions",
        "Advanced AI with custom models",
        "Priority support & live chat",
        "Advanced analytics & reporting",
        "Unlimited team members",
        "Full API access",
        "Custom integrations",
        "Enterprise security"
      ],
      gradient: "from-purple-500 to-pink-600",
      popular: true,
      badge: "Most Popular"
    },
    {
      name: "Pay-Per-Test",
      price: "$0.10",
      period: "/test",
      description: "Flexible pricing perfect for startups and small projects",
      features: [
        "Pay only for what you use",
        "No monthly commitments",
        "Basic AI test generation",
        "Email support",
        "Perfect for MVP testing",
        "Scale as you grow"
      ],
      gradient: "from-green-400 to-teal-600",
      popular: false,
      badge: "Startup Friendly"
    },
    {
      name: "Enterprise",
      price: "Custom",
      period: "",
      description: "Tailored solutions for large-scale deployments",
      features: [
        "Custom deployment options",
        "Dedicated AI model training",
        "24/7 dedicated support",
        "On-premise deployment",
        "Advanced security & compliance",
        "SLA guarantees",
        "Custom integrations",
        "Volume discounts available"
      ],
      gradient: "from-orange-500 to-red-600",
      popular: false,
      badge: "Enterprise"
    }
  ]

  const AnimatedCounter = ({ number, duration = 2 }) => {
    const [count, setCount] = useState(0)
    const ref = useRef(null)
    const isInView = useInView(ref, { once: true })

    useEffect(() => {
      if (isInView) {
        const target = parseInt(number.replace(/[^\d]/g, ''))
        const increment = target / (duration * 60)
        let current = 0
        const timer = setInterval(() => {
          current += increment
          if (current >= target) {
            setCount(target)
            clearInterval(timer)
          } else {
            setCount(Math.floor(current))
          }
        }, 1000 / 60)
        return () => clearInterval(timer)
      }
    }, [isInView, number, duration])

    return (
      <span ref={ref}>
        {number.includes('x') ? `${count}x` : 
         number.includes('+') ? `${count.toLocaleString()}+` :
         number.includes('%') ? `${count}%` : count.toLocaleString()}
      </span>
    )
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    setFormStatus('sending')
    
    try {
      const response = await fetch('https://api.web3forms.com/submit', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify({
          access_key: 'b339a4d7-a4a8-49c9-bf27-e8e651b6d246',
          ...formData
        })
      })
      
      const result = await response.json()
      if (result.success) {
        setFormStatus('success')
        setFormData({ name: '', email: '', company: '', message: '' })
        setTimeout(() => setShowDemoModal(false), 2000)
      } else {
        setFormStatus('error')
      }
    } catch (error) {
      setFormStatus('error')
    }
  }

  return (
    <div className="min-h-screen bg-black text-white overflow-hidden relative">
      {/* Advanced Animated Background */}
      <div className="fixed inset-0 overflow-hidden pointer-events-none">
        {/* Primary animated orbs */}
        <motion.div
          animate={{ 
            scale: [1, 1.3, 1],
            rotate: [0, 180, 360],
            opacity: [0.1, 0.4, 0.1]
          }}
          transition={{ duration: 25, repeat: Infinity, ease: "easeInOut" }}
          className="absolute -top-40 -right-40 w-96 h-96 bg-gradient-to-r from-purple-500 via-pink-500 to-blue-600 rounded-full blur-3xl"
        />
        <motion.div
          animate={{ 
            scale: [1.3, 1, 1.3],
            rotate: [360, 180, 0],
            opacity: [0.1, 0.3, 0.1]
          }}
          transition={{ duration: 30, repeat: Infinity, ease: "easeInOut" }}
          className="absolute -bottom-40 -left-40 w-[500px] h-[500px] bg-gradient-to-r from-orange-500 via-red-500 to-pink-500 rounded-full blur-3xl"
        />
        
        {/* Secondary floating particles */}
        {[...Array(20)].map((_, i) => (
          <motion.div
            key={i}
            animate={{
              y: [0, -100, 0],
              x: [0, Math.random() * 100 - 50, 0],
              opacity: [0, 1, 0],
            }}
            transition={{
              duration: Math.random() * 10 + 10,
              repeat: Infinity,
              delay: Math.random() * 10,
            }}
            className="absolute w-2 h-2 bg-blue-400 rounded-full"
            style={{
              left: `${Math.random() * 100}%`,
              top: `${Math.random() * 100}%`,
            }}
          />
        ))}

        {/* Mouse follower effect */}
        <motion.div
          animate={{
            x: mousePosition.x - 100,
            y: mousePosition.y - 100,
          }}
          transition={{ type: "spring", damping: 30, stiffness: 200 }}
          className="absolute w-48 h-48 bg-gradient-to-r from-blue-500/10 to-purple-500/10 rounded-full blur-2xl pointer-events-none"
        />
      </div>

      {/* Navigation */}
      <motion.nav 
        initial={{ y: -100 }}
        animate={{ y: 0 }}
        transition={{ duration: 0.8, ease: "easeOut" }}
        className="relative z-50 px-4 sm:px-6 py-4 bg-black/30 backdrop-blur-2xl border-b border-white/10"
      >
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          {/* Logo and Brand */}
          <motion.div 
            initial={{ opacity: 0, x: -30 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.2 }}
            className="flex items-center space-x-3"
          >
            <motion.div 
              whileHover={{ scale: 1.1, rotate: 360 }}
              transition={{ duration: 0.8 }}
              className="w-10 h-10 sm:w-12 sm:h-12 bg-gradient-to-r from-purple-500 via-pink-500 to-blue-600 rounded-2xl flex items-center justify-center shadow-2xl"
            >
              <LogoIcon className="h-5 w-5 sm:h-7 sm:w-7 text-white" />
            </motion.div>
            <div>
              <span className="text-xl sm:text-2xl font-bold bg-gradient-to-r from-purple-400 via-pink-400 to-blue-400 bg-clip-text text-transparent">
                TestCrew
              </span>
              <div className="flex items-center space-x-2">
                <div className="text-xs text-gray-400 font-medium hidden sm:block">AI Testing Platform</div>
                <motion.span 
                  animate={{ 
                    boxShadow: [
                      "0 0 10px rgba(147, 51, 234, 0.3)",
                      "0 0 20px rgba(147, 51, 234, 0.6)",
                      "0 0 10px rgba(147, 51, 234, 0.3)"
                    ]
                  }}
                  transition={{ duration: 3, repeat: Infinity }}
                  className="px-2 py-0.5 bg-gradient-to-r from-purple-500/20 via-pink-500/20 to-blue-500/20 border border-purple-500/30 rounded-full text-xs font-medium backdrop-blur-sm"
                >
                  Coming Soon
                </motion.span>
              </div>
            </div>
          </motion.div>
          
          {/* Desktop Navigation */}
          <motion.div 
            initial={{ opacity: 0, x: 30 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.4 }}
            className="hidden md:flex items-center space-x-4"
          >
            <motion.button 
              whileHover={{ scale: 1.05, y: -2 }}
              whileTap={{ scale: 0.95 }}
              onClick={() => setShowDemoModal(true)}
              className="flex items-center space-x-2 px-6 py-3 rounded-xl border border-white/20 hover:bg-white/10 transition-all duration-300 backdrop-blur-sm"
            >
              <Calendar className="h-4 w-4" />
              <span>Schedule Demo</span>
            </motion.button>
            <motion.button 
              whileHover={{ scale: 1.05, y: -2 }}
              whileTap={{ scale: 0.95 }}
              onClick={() => navigate('/login')}
              className="flex items-center space-x-2 px-6 py-3 rounded-xl border border-white/20 hover:bg-white/10 transition-all duration-300 backdrop-blur-sm"
            >
              <LogIn className="h-4 w-4" />
              <span>Login</span>
            </motion.button>
            <motion.button 
              whileHover={{ scale: 1.05, y: -2 }}
              whileTap={{ scale: 0.95 }}
              onClick={() => navigate('/register')}
              className="flex items-center space-x-2 px-6 py-3 bg-gradient-to-r from-purple-600 via-pink-600 to-blue-600 rounded-xl hover:from-purple-700 hover:via-pink-700 hover:to-blue-700 transition-all duration-300 shadow-2xl"
            >
              <UserPlus className="h-4 w-4" />
              <span>Get Started</span>
            </motion.button>
          </motion.div>

          {/* Mobile Menu Button */}
          <motion.button
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.4 }}
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            className="md:hidden p-2 rounded-xl border border-white/20 hover:bg-white/10 transition-all duration-300"
          >
            <div className="w-6 h-6 flex flex-col justify-center items-center space-y-1.5">
              <motion.span
                animate={isMobileMenuOpen ? { rotate: 45, y: 6 } : { rotate: 0, y: 0 }}
                className="w-5 h-0.5 bg-white rounded-full"
              />
              <motion.span
                animate={isMobileMenuOpen ? { opacity: 0 } : { opacity: 1 }}
                className="w-5 h-0.5 bg-white rounded-full"
              />
              <motion.span
                animate={isMobileMenuOpen ? { rotate: -45, y: -6 } : { rotate: 0, y: 0 }}
                className="w-5 h-0.5 bg-white rounded-full"
              />
            </div>
          </motion.button>
        </div>

        {/* Mobile Menu */}
        <AnimatePresence>
          {isMobileMenuOpen && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: "auto" }}
              exit={{ opacity: 0, height: 0 }}
              transition={{ duration: 0.3 }}
              className="md:hidden overflow-hidden"
            >
              <motion.div
                initial={{ y: -20 }}
                animate={{ y: 0 }}
                exit={{ y: -20 }}
                transition={{ duration: 0.3 }}
                className="py-4 space-y-4"
              >
                <motion.button 
                  whileTap={{ scale: 0.95 }}
                  onClick={() => {
                    setShowDemoModal(true)
                    setIsMobileMenuOpen(false)
                  }}
                  className="w-full flex items-center justify-center space-x-2 px-6 py-3 rounded-xl border border-white/20 hover:bg-white/10 transition-all duration-300 backdrop-blur-sm"
                >
                  <Calendar className="h-4 w-4" />
                  <span>Schedule Demo</span>
                </motion.button>
                <motion.button 
                  whileTap={{ scale: 0.95 }}
                  onClick={() => {
                    navigate('/login')
                    setIsMobileMenuOpen(false)
                  }}
                  className="w-full flex items-center justify-center space-x-2 px-6 py-3 rounded-xl border border-white/20 hover:bg-white/10 transition-all duration-300 backdrop-blur-sm"
                >
                  <LogIn className="h-4 w-4" />
                  <span>Login</span>
                </motion.button>
                <motion.button 
                  whileTap={{ scale: 0.95 }}
                  onClick={() => {
                    navigate('/register')
                    setIsMobileMenuOpen(false)
                  }}
                  className="w-full flex items-center justify-center space-x-2 px-6 py-3 bg-gradient-to-r from-purple-600 via-pink-600 to-blue-600 rounded-xl hover:from-purple-700 hover:via-pink-700 hover:to-blue-700 transition-all duration-300 shadow-2xl"
                >
                  <UserPlus className="h-4 w-4" />
                  <span>Get Started</span>
                </motion.button>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>
      </motion.nav>

      {/* Demo Modal */}
      <AnimatePresence>
        {showDemoModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm"
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              className="bg-black/90 border border-white/10 rounded-3xl p-8 max-w-lg w-full shadow-2xl"
            >
              <div className="flex justify-between items-center mb-6">
                <h3 className="text-2xl font-bold bg-gradient-to-r from-purple-400 via-pink-400 to-blue-400 bg-clip-text text-transparent">
                  Schedule a Demo
                </h3>
                <motion.button
                  whileHover={{ scale: 1.1, rotate: 90 }}
                  whileTap={{ scale: 0.9 }}
                  onClick={() => setShowDemoModal(false)}
                  className="text-gray-400 hover:text-white transition-colors"
                >
                  ✕
                </motion.button>
              </div>

              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <input
                    type="text"
                    placeholder="Your Name"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    required
                    className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl focus:outline-none focus:border-purple-500 transition-colors"
                  />
                </div>
                <div>
                  <input
                    type="email"
                    placeholder="Your Email"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    required
                    className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl focus:outline-none focus:border-purple-500 transition-colors"
                  />
                </div>
                <div>
                  <input
                    type="text"
                    placeholder="Company Name"
                    value={formData.company}
                    onChange={(e) => setFormData({ ...formData, company: e.target.value })}
                    required
                    className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl focus:outline-none focus:border-purple-500 transition-colors"
                  />
                </div>
                <div>
                  <textarea
                    placeholder="Additional Information"
                    value={formData.message}
                    onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                    rows="4"
                    className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl focus:outline-none focus:border-purple-500 transition-colors resize-none"
                  />
                </div>

                <motion.button
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  type="submit"
                  disabled={formStatus === 'sending'}
                  className="w-full py-4 bg-gradient-to-r from-purple-600 via-pink-600 to-blue-600 rounded-xl font-semibold disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {formStatus === 'sending' ? 'Sending...' : 
                   formStatus === 'success' ? '✓ Request Sent!' :
                   formStatus === 'error' ? 'Error - Try Again' :
                   'Schedule Demo'}
                </motion.button>
              </form>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Video Modal */}
      <AnimatePresence>
        {showVideoModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/90 backdrop-blur-sm"
            onClick={() => setShowVideoModal(false)}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              className="relative max-w-4xl w-full max-h-[80vh] bg-black rounded-3xl overflow-hidden shadow-2xl"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Close Button */}
              <motion.button
                whileHover={{ scale: 1.1, rotate: 90 }}
                whileTap={{ scale: 0.9 }}
                onClick={() => setShowVideoModal(false)}
                className="absolute top-4 right-4 z-10 w-10 h-10 bg-black/50 hover:bg-black/70 rounded-full flex items-center justify-center text-white transition-colors backdrop-blur-sm"
              >
                ✕
              </motion.button>
              
              {/* Video Player */}
              <div className="relative w-full h-full">
                <video
                  className="w-full h-full object-contain"
                  controls
                  autoPlay
                  muted
                  onEnded={() => setShowVideoModal(false)}
                >
                  <source src="/demo.mp4" type="video/mp4" />
                  Your browser does not support the video tag.
                </video>
              </div>
              
              {/* Video Title */}
              <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 to-transparent p-6">
                <h3 className="text-2xl font-bold text-white mb-2">
                  TestCrew AI Demo
                </h3>
                <p className="text-gray-300">
                  See how TestCrew AI revolutionizes your testing workflow
                </p>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Hero Section */}
      <motion.section 
        ref={heroRef}
        style={{ y, opacity }}
        className="relative z-10 px-6 pt-20 pb-32"
      >
        <div className="max-w-7xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            animate={{ opacity: isVisible ? 1 : 0, y: isVisible ? 0 : 50 }}
            transition={{ duration: 1, ease: "easeOut" }}
          >
            <motion.div 
              initial={{ scale: 0.8, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              transition={{ delay: 0.2, duration: 0.8 }}
              className="flex items-center justify-center mb-8"
            >
              <motion.span 
                animate={{ 
                  boxShadow: [
                    "0 0 20px rgba(147, 51, 234, 0.3)",
                    "0 0 60px rgba(147, 51, 234, 0.6)",
                    "0 0 20px rgba(147, 51, 234, 0.3)"
                  ]
                }}
                transition={{ duration: 3, repeat: Infinity }}
                className="px-6 py-3 bg-gradient-to-r from-purple-500/20 via-pink-500/20 to-blue-500/20 border border-purple-500/30 rounded-full text-sm font-medium backdrop-blur-sm"
              >
                🚀 Introducing your QA Assistant—Driven by AI.
              </motion.span>
            </motion.div>
            
            <motion.h1 
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4, duration: 0.8 }}
              className="text-6xl md:text-8xl font-bold mb-8 leading-tight"
            >
              Test Smarter with
              <motion.span 
                animate={{ 
                  backgroundPosition: ['0%', '100%', '0%']
                }}
                transition={{ duration: 5, repeat: Infinity, ease: "easeInOut" }}
                className="block bg-gradient-to-r from-purple-400 via-pink-400 via-blue-400 to-purple-400 bg-300% bg-clip-text text-transparent"
                style={{ backgroundSize: '300%' }}
              >
                TestCrew AI
              </motion.span>
            </motion.h1>
            
            <motion.p 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.6, duration: 0.8 }}
              className="text-xl md:text-2xl text-gray-300 mb-12 max-w-4xl mx-auto leading-relaxed"
            >
              Transform your testing workflow with AI that thinks like your best QA engineer. 
              Generate comprehensive test suites, execute natural language commands, and get 
              real-time insights—all without writing a single line of code.
            </motion.p>
            
            <motion.div 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.8, duration: 0.8 }}
              className="flex flex-col sm:flex-row items-center justify-center gap-6"
            >
              <motion.button
                whileHover={{ scale: 1.05, y: -5 }}
                whileTap={{ scale: 0.95 }}
                onClick={() => navigate('/register')}
                className="group relative flex items-center space-x-3 px-10 py-5 bg-gradient-to-r from-purple-600 via-pink-600 to-blue-600 rounded-2xl text-lg font-semibold shadow-2xl hover:shadow-purple-500/25 transition-all duration-500 overflow-hidden"
              >
                <motion.div
                  className="absolute inset-0 bg-gradient-to-r from-purple-700 via-pink-700 to-blue-700 opacity-0 group-hover:opacity-100 transition-opacity duration-500"
                />
                <Rocket className="h-6 w-6 relative z-10" />
                <span className="relative z-10">Start Free Trial</span>
                <ArrowRight className="h-5 w-5 relative z-10 group-hover:translate-x-1 transition-transform duration-300" />
              </motion.button>
              
              <motion.button
                whileHover={{ scale: 1.05, y: -5 }}
                whileTap={{ scale: 0.95 }}
                onClick={() => setShowVideoModal(true)}
                className="group flex items-center space-x-3 px-10 py-5 border-2 border-white/20 rounded-2xl text-lg font-semibold hover:bg-white/10 transition-all duration-500 backdrop-blur-sm"
              >
                <Play className="h-6 w-6 group-hover:scale-110 transition-transform duration-300" />
                <span>Watch Demo</span>
              </motion.button>
            </motion.div>

            {/* Floating Elements */}
            <div className="relative mt-20">
              {[...Array(6)].map((_, i) => (
                <motion.div
                  key={i}
                  animate={{
                    y: [0, -20, 0],
                    rotate: [0, 5, -5, 0],
                  }}
                  transition={{
                    duration: 4 + i,
                    repeat: Infinity,
                    delay: i * 0.5,
                  }}
                  className="absolute"
                  style={{
                    left: `${20 + i * 12}%`,
                    top: `${Math.random() * 100}px`,
                  }}
                >
                  <div className="w-16 h-16 bg-gradient-to-r from-purple-500/20 to-pink-500/20 rounded-2xl backdrop-blur-sm border border-white/10 flex items-center justify-center">
                    {[Bot, Cpu, Database, Workflow, TestTube, Activity][i] && 
                      React.createElement([Bot, Cpu, Database, Workflow, TestTube, Activity][i], { 
                        className: "h-8 w-8 text-purple-400" 
                      })
                    }
                  </div>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </div>
      </motion.section>

      {/* Enhanced Features Section */}
      <section className="relative z-10 px-6 py-32">
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center mb-20"
          >
            <h2 className="text-5xl md:text-6xl font-bold mb-6">
              Powerful Features That
              <motion.span 
                animate={{ 
                  backgroundPosition: ['0%', '100%', '0%']
                }}
                transition={{ duration: 4, repeat: Infinity }}
                className="block bg-gradient-to-r from-green-400 via-blue-400 to-purple-400 bg-300% bg-clip-text text-transparent"
                style={{ backgroundSize: '300%' }}
              >
                Revolutionize Testing
              </motion.span>
            </h2>
          </motion.div>
          
          <div className="grid md:grid-cols-2 gap-16 items-center">
            <motion.div
              initial={{ opacity: 0, x: -50 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
            >
              <AnimatePresence mode="wait">
                <motion.div
                  key={currentFeature}
                  initial={{ opacity: 0, x: -30, scale: 0.9 }}
                  animate={{ opacity: 1, x: 0, scale: 1 }}
                  exit={{ opacity: 0, x: 30, scale: 0.9 }}
                  transition={{ duration: 0.6, ease: "easeInOut" }}
                  className="space-y-8"
                >
                  <motion.div 
                    whileHover={{ scale: 1.1, rotate: 5 }}
                    className={`w-20 h-20 bg-gradient-to-r ${features[currentFeature].gradient} rounded-3xl flex items-center justify-center shadow-2xl`}
                  >
                    {(() => {
                      const IconComponent = features[currentFeature].icon
                      return <IconComponent className="h-10 w-10 text-white" />
                    })()}
                  </motion.div>
                  
                  <h3 className="text-4xl font-bold leading-tight">{features[currentFeature].title}</h3>
                  <p className="text-xl text-gray-300 leading-relaxed">
                    {features[currentFeature].description}
                  </p>
                  
                  <motion.button 
                    whileHover={{ scale: 1.05, y: -2 }}
                    whileTap={{ scale: 0.95 }}
                    onClick={() => {
                      const emojis = ['🚀', '✨', '🎯', '💫', '🌟', '⚡', '🎉', '🎨', '🎭', '🎪']
                      const randomEmoji = emojis[Math.floor(Math.random() * emojis.length)]
                      alert(`${randomEmoji} Coming Soon! ${randomEmoji}`)
                    }}
                    className="flex items-center space-x-3 text-purple-400 hover:text-purple-300 transition-colors text-lg font-medium group"
                  >
                    <span>Coming Soon</span>
                    <motion.span
                      animate={{ 
                        rotate: [0, 10, -10, 0],
                        scale: [1, 1.2, 1]
                      }}
                      transition={{ duration: 0.5 }}
                      className="group-hover:animate-bounce"
                    >
                      🚀
                    </motion.span>
                  </motion.button>
                </motion.div>
              </AnimatePresence>
            </motion.div>
            
            <motion.div
              initial={{ opacity: 0, x: 50 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
              className="relative"
            >
              <motion.div 
                animate={{ y: [0, -10, 0] }}
                transition={{ duration: 6, repeat: Infinity }}
                className="bg-black/60 backdrop-blur-2xl rounded-3xl border border-white/10 p-8 shadow-2xl"
              >
                <div className="space-y-6">
                  <div className="flex items-center space-x-3 text-green-400">
                    <motion.div 
                      animate={{ scale: [1, 1.2, 1] }}
                      transition={{ duration: 2, repeat: Infinity }}
                      className="w-4 h-4 bg-green-400 rounded-full"
                    />
                    <span className="font-mono text-sm">TestCrew AI Active</span>
                  </div>
                  
                  <div className="bg-gray-900/50 rounded-2xl p-6 font-mono text-sm space-y-3">
                    <motion.div 
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: 0.5 }}
                      className="text-purple-400"
                    >
                      $ testcrew analyze --url https://myapp.com
                    </motion.div>
                    <motion.div 
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: 1 }}
                      className="text-gray-400"
                    >
                      🤖 Analyzing application architecture...
                    </motion.div>
                    <motion.div 
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: 1.5 }}
                      className="text-gray-400"
                    >
                      🔍 Mapping user flows and interactions...
                    </motion.div>
                    <motion.div 
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: 2 }}
                      className="text-green-400"
                    >
                      ✅ Generated 24 comprehensive test scenarios
                    </motion.div>
                  </div>
                  
                  <div className="grid grid-cols-3 gap-3">
                    {[1, 2, 3].map((i) => (
                      <motion.div 
                        key={i}
                        initial={{ opacity: 0, scale: 0.8 }}
                        animate={{ opacity: 1, scale: 1 }}
                        transition={{ delay: 2.5 + i * 0.2 }}
                        whileHover={{ scale: 1.05 }}
                        className="bg-gradient-to-br from-purple-500/20 to-pink-500/20 rounded-xl p-4 border border-purple-500/30"
                      >
                        <Image className="h-6 w-6 text-purple-400 mb-2" />
                        <div className="text-xs text-gray-300">Test {i} ✓</div>
                      </motion.div>
                    ))}
                  </div>
                </div>
              </motion.div>

              {/* Floating UI Elements */}
              <motion.div
                animate={{ 
                  rotate: [0, 360],
                  scale: [1, 1.1, 1]
                }}
                transition={{ duration: 20, repeat: Infinity }}
                className="absolute -top-6 -right-6 w-12 h-12 bg-gradient-to-r from-pink-500 to-purple-600 rounded-2xl flex items-center justify-center"
              >
                <Sparkles className="h-6 w-6 text-white" />
              </motion.div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Enhanced How It Works */}
      <section className="relative z-10 px-6 py-32 bg-gradient-to-r from-black/40 to-purple-900/20">
        <div className="max-w-7xl mx-auto">
          <motion.h2 
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            className="text-5xl md:text-6xl font-bold text-center mb-20"
          >
            How TestCrew Works
          </motion.h2>
          
          <div className="grid md:grid-cols-3 gap-12">
            {[
              {
                step: "01",
                icon: Globe,
                title: "Connect Your Application",
                description: "Simply provide your website URL or upload your application details. TestCrew's AI begins analyzing immediately.",
                color: "from-blue-500 to-cyan-500"
              },
              {
                step: "02", 
                icon: Brain,
                title: "AI Generates Test Suite",
                description: "Our advanced AI understands your application flow and automatically creates comprehensive test scenarios covering all critical paths.",
                color: "from-purple-500 to-pink-500"
              },
              {
                step: "03",
                icon: Eye,
                title: "Execute & Monitor",
                description: "Watch as TestCrew executes tests in real-time with live screenshots, detailed logs, and instant results.",
                color: "from-green-500 to-teal-500"
              }
            ].map((item, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 50 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.3, duration: 0.8 }}
                whileHover={{ y: -10, scale: 1.02 }}
                className="relative group"
              >
                <div className="bg-black/40 backdrop-blur-2xl rounded-3xl border border-white/10 p-8 hover:bg-black/60 transition-all duration-500 group-hover:border-white/20">
                  <div className="flex items-center mb-8">
                    <motion.span 
                      animate={{ 
                        textShadow: [
                          "0 0 10px rgba(147, 51, 234, 0.5)",
                          "0 0 20px rgba(147, 51, 234, 0.8)",
                          "0 0 10px rgba(147, 51, 234, 0.5)"
                        ]
                      }}
                      transition={{ duration: 3, repeat: Infinity }}
                      className="text-5xl font-bold text-gray-500 mr-6"
                    >
                      {item.step}
                    </motion.span>
                    <motion.div 
                      whileHover={{ rotate: 360, scale: 1.1 }}
                      transition={{ duration: 0.8 }}
                      className={`w-16 h-16 bg-gradient-to-r ${item.color} rounded-2xl flex items-center justify-center shadow-2xl`}
                    >
                      <item.icon className="h-8 w-8 text-white" />
                    </motion.div>
                  </div>
                  
                  <h3 className="text-3xl font-bold mb-6 group-hover:text-purple-300 transition-colors">{item.title}</h3>
                  <p className="text-gray-300 leading-relaxed text-lg">{item.description}</p>
                </div>
                
                {index < 2 && (
                  <motion.div 
                    animate={{ x: [0, 10, 0] }}
                    transition={{ duration: 3, repeat: Infinity }}
                    className="hidden md:block absolute top-1/2 -right-6 w-12 h-12 border-t-2 border-r-2 border-purple-400/60 transform rotate-45 -translate-y-1/2"
                  />
                )}
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Enhanced Stats Section
      <section className="relative z-10 px-6 py-32">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-12">
            {stats.map((stat, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, scale: 0.5, y: 50 }}
                whileInView={{ opacity: 1, scale: 1, y: 0 }}
                transition={{ delay: stat.delay, duration: 0.8, type: "spring" }}
                whileHover={{ scale: 1.1, y: -10 }}
                className="text-center group cursor-pointer"
              >
                <motion.div 
                  whileHover={{ rotate: 360 }}
                  transition={{ duration: 0.8 }}
                  className="w-20 h-20 bg-gradient-to-r from-purple-500 via-pink-500 to-blue-600 rounded-3xl flex items-center justify-center mx-auto mb-6 shadow-2xl group-hover:shadow-purple-500/50"
                >
                  <stat.icon className="h-10 w-10 text-white" />
                </motion.div>
                <motion.div className="text-4xl md:text-5xl font-bold mb-3 bg-gradient-to-r from-purple-400 to-pink-400 bg-clip-text text-transparent">
                  <AnimatedCounter number={stat.number} />
                </motion.div>
                <div className="text-gray-400 text-lg font-medium">{stat.label}</div>
              </motion.div>
            ))}
          </div>
        </div>
      </section> */}

      {/* Enhanced Testimonials
      <section className="relative z-10 px-6 py-32 bg-gradient-to-r from-purple-900/20 to-black/40">
        <div className="max-w-7xl mx-auto">
          <motion.h2 
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            className="text-5xl md:text-6xl font-bold text-center mb-20"
          >
            What Our Users Say
          </motion.h2>
          
          <div className="grid md:grid-cols-3 gap-8">
            {testimonials.map((testimonial, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 50, rotateY: -15 }}
                whileInView={{ opacity: 1, y: 0, rotateY: 0 }}
                transition={{ delay: index * 0.2, duration: 0.8 }}
                whileHover={{ 
                  y: -10, 
                  scale: 1.03,
                  rotateY: 5,
                  boxShadow: "0 25px 50px rgba(147, 51, 234, 0.3)"
                }}
                className="bg-black/40 backdrop-blur-2xl rounded-3xl border border-white/10 p-8 hover:bg-black/60 transition-all duration-500 transform-gpu"
                style={{ perspective: "1000px" }}
              >
                <div className="flex items-center mb-6">
                  {[...Array(testimonial.rating)].map((_, i) => (
                    <motion.div
                      key={i}
                      initial={{ opacity: 0, scale: 0 }}
                      animate={{ opacity: 1, scale: 1 }}
                      transition={{ delay: index * 0.2 + i * 0.1 }}
                    >
                      <Star className="h-6 w-6 text-yellow-400 fill-current" />
                    </motion.div>
                  ))}
                </div>
                
                <motion.p 
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: index * 0.2 + 0.3 }}
                  className="text-gray-300 mb-8 italic text-lg leading-relaxed"
                >
                  "{testimonial.content}"
                </motion.p>
                
                <div className="flex items-center">
                  <motion.div 
                    whileHover={{ scale: 1.2, rotate: 10 }}
                    className="text-4xl mr-4"
                  >
                    {testimonial.avatar}
                  </motion.div>
                  <div>
                    <div className="font-semibold text-lg">{testimonial.name}</div>
                    <div className="text-sm text-gray-400">{testimonial.role}</div>
                    <div className="text-xs text-purple-400 font-medium">{testimonial.company}</div>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section> */}

      {/* Enhanced Pricing */}
      <section className="relative z-10 px-6 py-32">
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            className="text-center mb-20"
          >
            <h2 className="text-5xl md:text-6xl font-bold mb-6">
              Simple, Transparent Pricing
            </h2>
            <p className="text-xl text-gray-300">
              Choose the perfect plan for your testing needs
            </p>
          </motion.div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {pricingPlans.map((plan, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 50, scale: 0.9 }}
                whileInView={{ opacity: 1, y: 0, scale: 1 }}
                transition={{ delay: index * 0.1, duration: 0.8 }}
                whileHover={{ 
                  y: -15, 
                  scale: plan.popular ? 1.05 : 1.03,
                  boxShadow: plan.popular ? 
                    "0 25px 50px rgba(147, 51, 234, 0.4)" : 
                    "0 15px 30px rgba(255, 255, 255, 0.1)"
                }}
                className={`relative bg-black/40 backdrop-blur-2xl rounded-3xl border p-6 hover:bg-black/60 transition-all duration-500 ${
                  plan.popular ? 'border-purple-500 scale-105' : 'border-white/10'
                }`}
              >
                {plan.badge && (
                  <motion.div 
                    initial={{ opacity: 0, y: -10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: index * 0.1 + 0.3 }}
                    className="absolute -top-4 left-1/2 transform -translate-x-1/2"
                  >
                    <span className={`px-4 py-1 rounded-full text-xs font-semibold ${
                      plan.popular 
                        ? 'bg-gradient-to-r from-purple-500 to-pink-500 text-white' 
                        : plan.badge === 'Startup Friendly'
                        ? 'bg-gradient-to-r from-green-500 to-teal-500 text-white'
                        : 'bg-gradient-to-r from-orange-500 to-red-500 text-white'
                    }`}>
                      {plan.badge}
                    </span>
                  </motion.div>
                )}
                
                <div className="text-center mb-6">
                  <h3 className="text-2xl font-bold mb-3">{plan.name}</h3>
                  <div className="flex items-baseline justify-center mb-3">
                    <motion.span 
                      initial={{ scale: 0 }}
                      animate={{ scale: 1 }}
                      transition={{ delay: index * 0.1 + 0.5, type: "spring" }}
                      className="text-4xl font-bold bg-gradient-to-r from-purple-400 to-pink-400 bg-clip-text text-transparent"
                    >
                      {plan.price}
                    </motion.span>
                    <span className="text-gray-400 ml-1 text-base">{plan.period}</span>
                  </div>
                  <p className="text-gray-400 text-sm">{plan.description}</p>
                </div>
                
                <ul className="space-y-3 mb-6">
                  {plan.features.map((feature, i) => (
                    <motion.li 
                      key={i}
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: index * 0.1 + i * 0.05 }}
                      className="flex items-start"
                    >
                      <CheckCircle className="h-4 w-4 text-green-400 mr-2 flex-shrink-0 mt-0.5" />
                      <span className="text-gray-300 text-sm">{feature}</span>
                    </motion.li>
                  ))}
                </ul>
                
                <motion.button 
                  whileHover={{ scale: 1.05, y: -2 }}
                  whileTap={{ scale: 0.95 }}
                  className={`w-full py-3 rounded-xl font-semibold transition-all duration-300 text-sm ${
                    plan.popular 
                      ? 'bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600 text-white shadow-2xl' 
                      : 'border-2 border-white/20 hover:bg-white/10 text-white'
                  }`}
                  onClick={() => plan.price === "Custom" ? window.open('mailto:sales@testcrew.ai') : navigate('/register')}
                >
                  {plan.price === "Custom" ? "Contact Sales" : "Get Started"}
                </motion.button>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Enhanced CTA Section */}
      <section className="relative z-10 px-6 py-32 bg-gradient-to-r from-purple-600/20 via-pink-600/20 to-blue-600/20">
        <div className="max-w-4xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <motion.h2 
              animate={{ 
                backgroundPosition: ['0%', '100%', '0%']
              }}
              transition={{ duration: 5, repeat: Infinity }}
              className="text-5xl md:text-6xl font-bold mb-8 bg-gradient-to-r from-purple-400 via-pink-400 to-blue-400 bg-300% bg-clip-text text-transparent"
              style={{ backgroundSize: '300%' }}
            >
              Ready to Transform Your Testing?
            </motion.h2>
            
           <p></p>
            
            <div className="flex flex-col sm:flex-row items-center justify-center gap-8">
              <motion.button
                whileHover={{ scale: 1.05, y: -5 }}
                whileTap={{ scale: 0.95 }}
                onClick={() => navigate('/register')}
                className="group relative flex items-center space-x-3 px-12 py-6 bg-gradient-to-r from-purple-600 via-pink-600 to-blue-600 rounded-2xl text-xl font-semibold shadow-2xl hover:shadow-purple-500/25 transition-all duration-500 overflow-hidden"
              >
                <motion.div
                  className="absolute inset-0 bg-gradient-to-r from-purple-700 via-pink-700 to-blue-700 opacity-0 group-hover:opacity-100 transition-opacity duration-500"
                />
                <Sparkles className="h-7 w-7 relative z-10" />
                <span className="relative z-10">Start Your Free Trial</span>
                <ArrowRight className="h-6 w-6 relative z-10 group-hover:translate-x-1 transition-transform duration-300" />
              </motion.button>
              
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.5 }}
                className="text-gray-400 text-center"
              >
                <div className="text-sm">No credit card required</div>
                <div className="text-sm">14-day free trial • Cancel anytime</div>
              </motion.div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Enhanced Footer */}
      <footer className="relative z-10 px-6 py-16 bg-black/60 border-t border-white/10 backdrop-blur-2xl">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-col md:flex-row items-center justify-between">
            <motion.div 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="flex items-center space-x-4 mb-6 md:mb-0"
            >
              <motion.div 
                whileHover={{ scale: 1.1, rotate: 360 }}
                transition={{ duration: 0.8 }}
                className="w-12 h-12 bg-gradient-to-r from-purple-500 via-pink-500 to-blue-600 rounded-2xl flex items-center justify-center shadow-2xl"
              >
                <LogoIcon className="h-6 w-6 text-white" />
              </motion.div>
              <div>
                <span className="text-2xl font-bold bg-gradient-to-r from-purple-400 via-pink-400 to-blue-400 bg-clip-text text-transparent">
                  TestCrew
                </span>
                <div className="text-xs text-gray-400">AI Testing Platform</div>
              </div>
            </motion.div>
            
            <motion.div 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="text-gray-400 text-center"
            >
              <div>© 2025 TestCrew. All rights reserved.</div>
              <div className="text-xs mt-1">Built with ❤️ for Developers and QA teams</div>
            </motion.div>
          </div>
        </div>
      </footer>
    </div>
  )
}

export default TestCrewLanding
